'use client';

import { useState } from 'react';

import { Info, X } from 'lucide-react';

import { cn } from '@kit/ui/utils';

function InfoExplainer({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);

  return (
    <span className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="text-slate-500 transition-colors hover:text-slate-600"
        aria-label="Wyjaśnienie"
      >
        <Info className="h-2.5 w-2.5" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute top-full left-0 z-50 mt-1.5 w-56 rounded-lg border-2 border-[#d5d0c8] bg-[#fffef9] p-3 text-xs leading-relaxed text-slate-600 shadow-[3px_3px_0_0_#d5d0c8]">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="absolute top-1.5 right-1.5 text-slate-300 hover:text-slate-500"
            >
              <X className="h-3 w-3" />
            </button>
            {children}
          </div>
        </>
      )}
    </span>
  );
}

export function DrugExplainer({
  drugs,
  pairs,
}: {
  drugs: number;
  pairs: number;
}) {
  return (
    <InfoExplainer>
      <p>
        Każdy lek porównujemy z każdym innym, więc im więcej leków, tym więcej
        par do sprawdzenia.
      </p>
      <p className="mt-2 text-slate-500">
        <span className="font-semibold text-slate-700">Przykład:</span> {drugs}{' '}
        leków = {pairs} par do zbadania.
      </p>
    </InfoExplainer>
  );
}

function AnalysisExplainer({
  maxDrugs,
  pairs,
}: {
  maxDrugs: number;
  pairs: number;
}) {
  return (
    <InfoExplainer>
      <p>
        Każde kliknięcie &bdquo;
        <em className="text-slate-700">Sprawdź interakcje</em>&rdquo; to
        1&nbsp;analiza, niezależnie od liczby leków na liście.
      </p>
      <p className="mt-2 text-slate-500">
        <span className="font-semibold text-slate-700">Przykład:</span>{' '}
        {maxDrugs} leków = {pairs} par do zbadania = 1&nbsp;analiza.
      </p>
      <p className="mt-1.5 text-[10px] text-slate-400">
        Limit odnawia się co miesiąc.
      </p>
    </InfoExplainer>
  );
}

/** Monthly checks + max drugs block shared by tier and dedicated plan cards. */
export function Features({
  maxDrugs,
  pairs,
  monthlyChecks,
  highlighted = false,
}: {
  maxDrugs: number;
  pairs: number;
  monthlyChecks: number | null;
  highlighted?: boolean;
}) {
  return (
    <div
      className={cn(
        'mt-5 border-t border-dashed pt-4',
        highlighted ? 'border-slate-300' : 'border-slate-200',
      )}
    >
      <div className="flex flex-col gap-4">
        <div>
          <div className="flex items-center gap-1">
            <p className="text-[10px] font-medium tracking-wider text-slate-400 uppercase">
              Analiz miesięcznie
            </p>
            <AnalysisExplainer maxDrugs={maxDrugs} pairs={pairs} />
          </div>
          <div className="mt-0.5 flex items-baseline gap-2">
            <p className="text-lg font-bold text-slate-800">
              <span className="relative">
                <span className="absolute right-0 bottom-0 left-0 h-[0.4em] bg-green-300/50" />
                <span className="relative">
                  {monthlyChecks === null ? 'Bez limitu' : monthlyChecks}
                </span>
              </span>
            </p>
          </div>
        </div>
        <div>
          <div className="flex items-center gap-1">
            <p className="text-[10px] font-medium tracking-wider text-slate-400 uppercase">
              Leków jednocześnie
            </p>
            <DrugExplainer drugs={maxDrugs} pairs={pairs} />
          </div>
          <div className="mt-0.5 flex items-baseline gap-2">
            <p className="text-lg font-bold text-slate-800">
              <span className="relative">
                <span className="absolute right-0 bottom-0 left-0 h-[0.4em] bg-green-300/50" />
                <span className="relative">do {maxDrugs}</span>
              </span>
            </p>
            <span className="text-[11px] text-slate-400">({pairs} par)</span>
          </div>
        </div>
      </div>
    </div>
  );
}
